import React, { useState } from "react";
import { View, StyleSheet, Pressable, Text } from "react-native";
import type { PressableStateCallbackType } from "react-native";
import { Chat, GraduationCap, BuildingOffice, GearSix, ListChecks } from "@phosphor-icons/react";
import AxioButton from "../../axio-ds/Button/AxioButton";
import AxioCard from "../../axio-ds/Card/AxioCard";
import { colors, radii, spacing } from "../../../styles/design-system";

// this will probably be an enum defined by the schema,
// so we will be able to import it from generated graphql types
export type WorkspaceType = "general" | "course" | "support" | "todo" | "settings";

// in the actual implementation, instead of passing id, title, type, lastActivity, threadCount
// as separate props, we should pass the actual entity and destructure it here
export interface WorkspaceCardProps {
  id?: string;
  title: string;
  type: WorkspaceType;
  lastActivityLabel?: string;
  unreadCount?: number;
  threadCount?: number;
  isActive?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  onThreadsPress?: () => void;
}

const iconSize = 20;

const getIconForType = (type: WorkspaceType, color: string) => {
  if (type === "course") {
    return <GraduationCap size={iconSize} color={color} />;
  }
  if (type === "support") {
    return <BuildingOffice size={iconSize} color={color} />;
  }
  if (type === "todo") {
    return <ListChecks size={iconSize} color={color} />;
  }
  if (type === "settings") {
    return <GearSix size={iconSize} color={color} />;
  }
  return <Chat size={iconSize} color={color} />;
};

const WorkspaceCard: React.FC<WorkspaceCardProps> = ({
  title,
  type,
  lastActivityLabel,
  unreadCount,
  threadCount,
  isActive = false,
  disabled = false,
  onPress,
  onThreadsPress,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const handlePress = () => {
    if (!disabled && onPress) {
      onPress();
    }
  };

  const handleThreadsPress = () => {
    if (!disabled && onThreadsPress) {
      onThreadsPress();
    }
  };

  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={{ borderRadius: radii.lg }}>
      {(state: PressableStateCallbackType) => (
        <AxioCard
          variant="workspace"
          isActive={isActive}
          disabled={disabled}
          hovered={isHovered && !state.pressed}
          pressed={state.pressed}>
          {/* Left: icon + text */}
          <View style={styles.leftSection}>
            <View style={[styles.iconContainer, isActive && styles.iconContainerActive]}>
              {getIconForType(type, isActive ? colors.brand[700] : colors.grey[500])}
            </View>
            <View style={styles.textContainer}>
              <Text
                style={[styles.titleText, isActive ? styles.titleTextActive : styles.titleTextInactive]}
                numberOfLines={1}>
                {title}
              </Text>
              {lastActivityLabel ? (
                <Text style={styles.subtitleText} numberOfLines={1}>
                  {lastActivityLabel}
                </Text>
              ) : null}
              {isActive && typeof threadCount === "number" && threadCount > 0 ? (
                <View style={styles.threadButtonWrapper}>
                  <AxioButton
                    label={`${threadCount} thread${threadCount === 1 ? "" : "s"}`}
                    size="sm"
                    variant="tinted"
                    onPress={handleThreadsPress}
                  />
                </View>
              ) : null}
            </View>
          </View>

          {/* Right: unread badge (inactive only) */}
          <View style={styles.rightSection}>
            {!isActive && typeof unreadCount === "number" && unreadCount > 0 ? (
              <View style={styles.unreadBadge}>
                <Text style={styles.unreadBadgeText}>{unreadCount}</Text>
              </View>
            ) : null}
          </View>
        </AxioCard>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  leftSection: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  rightSection: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: spacing.xs,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: radii.lg,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[100],
  },
  iconContainerActive: {
    backgroundColor: colors.brand[100],
  },
  textContainer: {
    flexDirection: "column",
    marginLeft: spacing.xs,
    minWidth: 0,
  },
  titleText: {
    fontSize: 14,
    fontWeight: "600",
  },
  titleTextActive: {
    color: colors.brand[700],
  },
  titleTextInactive: {
    color: colors.grey[700],
  },
  subtitleText: {
    fontSize: 12,
    color: colors.grey[500],
  },
  threadButtonWrapper: {
    marginTop: spacing.xs,
    alignSelf: "flex-start",
  },
  unreadBadge: {
    minWidth: 24,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: colors.brand[500],
    alignItems: "center",
    justifyContent: "center",
  },
  unreadBadgeText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.white,
  },
});
export default WorkspaceCard;
